import React from "react";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { Trophy } from "lucide-react";

const MasteryScore = ({ tradeData, trades, userSettings, skillsData }) => {
  // 2. Use the skills data from Development Hub if available, otherwise calculate locally
  const calculateSkillsAssessment = (trades) => {
    const calculateConsistencyScore = (trades) => {
      if (!trades || trades.length === 0) return 0;
      const pnls = trades
        .filter((trade) => typeof trade.pnl === "number")
        .map((t) => t.pnl);
      if (pnls.length === 0) return 0;
      const mean = pnls.reduce((sum, pnl) => sum + pnl, 0) / pnls.length;
      const variance =
        pnls.reduce((sum, pnl) => sum + Math.pow(pnl - mean, 2), 0) /
        pnls.length;
      const stdDev = Math.sqrt(variance);
      const consistencyRatio = Math.abs(mean) / (stdDev || 1);
      return Math.min(Math.round((consistencyRatio / 2) * 100), 100);
    };

    const calculateProfitabilityScore = (trades) => {
      if (!trades || trades.length === 0) return 0;
      const profitFactor = tradeData.profitFactor || 0;
      if (profitFactor >= 3) return 100;
      if (profitFactor >= 2.5) return 90;
      if (profitFactor >= 2) return 80;
      if (profitFactor >= 1.5) return 70;
      if (profitFactor >= 1.25) return 60;
      if (profitFactor >= 1) return 50;
      return Math.max(0, Math.round((profitFactor / 1) * 50));
    };

    const calculateDisciplineScore = (trades) => {
      if (!trades || trades.length === 0) return 0;
      const dailyResults = trades.reduce((acc, trade) => {
        const date = new Date(trade.date).toDateString();
        if (!acc[date]) acc[date] = [];
        acc[date].push(trade);
        return acc;
      }, {});
      const totalDays = Object.keys(dailyResults).length;
      if (totalDays === 0) return 0;
      const winningDays = Object.values(dailyResults).filter((dayTrades) => {
        const dayPnL = dayTrades.reduce(
          (sum, trade) => sum + (trade.pnl || 0),
          0
        );
        return dayPnL > 0;
      }).length;
      return Math.round((winningDays / totalDays) * 100);
    };

    const calculateStabilityScore = (trades) => {
      if (!trades || trades.length === 0) return 0;
      const pnls = trades
        .filter((trade) => typeof trade.pnl === "number")
        .map((t) => t.pnl);
      if (pnls.length === 0) return 0;
      const mean = pnls.reduce((sum, pnl) => sum + pnl, 0) / pnls.length;
      const stdDev = Math.sqrt(
        pnls.reduce((sum, pnl) => sum + Math.pow(pnl - mean, 2), 0) /
          pnls.length
      );
      const outliers = pnls.filter((pnl) => Math.abs(pnl - mean) > 2 * stdDev);
      const outlierPenalty = outliers.reduce((penalty, pnl) => {
        const deviations = Math.abs(pnl - mean) / stdDev;
        return penalty + (deviations - 2) * 5;
      }, 0);
      return Math.max(0, Math.min(100, 100 - Math.round(outlierPenalty)));
    };

    const calculateProbabilityScore = (trades) => {
      if (!trades || trades.length === 0) return 0;
      const validTrades = trades.filter(
        (trade) => typeof trade.pnl === "number"
      );
      if (validTrades.length === 0) return 0;
      const winningTrades = validTrades.filter((trade) => trade.pnl > 0).length;
      return Math.round((winningTrades / validTrades.length) * 100);
    };

    const consistency = calculateConsistencyScore(trades);
    const profitability = calculateProfitabilityScore(trades);
    const discipline = calculateDisciplineScore(trades);
    const stability = calculateStabilityScore(trades);
    const probability = calculateProbabilityScore(trades);

    const overallScore = Math.round(
      (consistency + profitability + discipline + stability + probability) / 5
    );

    return {
      consistency,
      profitability,
      discipline,
      stability,
      probability,
      overallScore,
    };
  };

  const skills = skillsData || calculateSkillsAssessment(trades);

  // 3. Update the radar chart data
  const radarData = [
    { category: "Consistency", value: skills.consistency || 0, fullMark: 100 },
    { category: "Probability", value: skills.probability || 0, fullMark: 100 },
    {
      category: "Profitability",
      value: skills.profitability || 0,
      fullMark: 100,
    },
    { category: "Stability", value: skills.stability || 0, fullMark: 100 },
    { category: "Discipline", value: skills.discipline || 0, fullMark: 100 },
  ];

  // 4. Use the overall score from skills data
  const masteryScore = skills.overallScore || 0;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="mastery-tooltip">
          <p>{`${payload[0].payload.category}: ${payload[0].value}%`}</p>
        </div>
      );
    }
    return null;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return "#10b981";
    if (score >= 70) return "#2563eb";
    if (score >= 50) return "#f59e0b";
    return "#ef4444";
  };

  return (
    <div className="mastery-score-container">
      <div className="mastery-score-header">
        <div className="score-title">
          <Trophy size={20} className="score-icon" />
          <h3>Mastery Score</h3>
        </div>
        <div
          className="mastery-score-value"
          style={{ color: getScoreColor(masteryScore) }}
        >
          {masteryScore}
        </div>
      </div>

      <div className="radar-chart-container">
        <ResponsiveContainer width="100%" height={230}>
          <RadarChart
            data={radarData}
            margin={{ top: 0, right: 30, bottom: 0, left: 30 }}
          >
            <PolarGrid gridType="polygon" />
            <PolarAngleAxis
              dataKey="category"
              tick={{ fill: "var(--text-secondary)", fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Radar
              name="Grades"
              dataKey="value"
              stroke={getScoreColor(masteryScore)}
              fill={getScoreColor(masteryScore)}
              fillOpacity={0.3}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      <div
        className="metric-subtitle"
        style={{
          color: "var(--text-secondary)",
          textAlign: "center",
          marginTop: "-2.3rem",
        }}
      >
        Mastery Score becomes more accurate as the sample size of your trade
        examples increase
      </div>
    </div>
  );
};

export default MasteryScore;
